// @ts-nocheck
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { generateClient } from "aws-amplify/api";
import {
  listComposedForecastsCustom,
  createComposedForecastCustom,
  createComposedForecastDataSourceCustom,
  updateComposedForecastCustom,
  updateComposedForecastDataSourceCustom,
  deleteComposedForecastCustom,
  deleteComposedForecastDataSourceCustom,
  composedForecastAssetsByAssetCustom,
  createComposedForecastAssetsCustom,
  updateComposedForecastAssetsCustom,
  deleteComposedForecastAssetsCustom,
} from "../graphql/customQueries";
import {
  diffComposedForecastLinks,
  type SelectedComposedForecast,
  type ComposedForecastAssetLinkRow,
} from "../utils/composedForecastDashboardUtils";
import type {
  ComposedForecast,
  DataSourceFormState,
} from "../types/composedForecast";
import type { SourceDisplaySettings } from "../utils/composedForecastDisplay";
import { normalizeWeight } from "../utils/composedForecastWeight";
import {
  buildCopyDataSourceInput,
  buildCopyForecastInput,
  type CopyProgress,
} from "../utils/composedForecastCopy";
import type { ForecastOwnership } from "../utils/composedForecastOwnership";
import { partialGraphqlData } from "../utils/graphqlPartial";

const client = generateClient();

interface ComposedForecastState {
  forecasts: Record<string, ComposedForecast>;
  loading: "idle" | "pending" | "success" | "error";
  error: string | null;
}

const initialState: ComposedForecastState = {
  forecasts: {},
  loading: "idle",
  error: null,
};

// Fetch all composed forecasts for the current user
export const fetchComposedForecasts = createAsyncThunk(
  "composedForecasts/fetchAll",
  async (_, thunkAPI) => {
    try {
      let data;
      try {
        ({ data } = await client.graphql({
          query: listComposedForecastsCustom,
        }));
      } catch (error) {
        // A linked asset or source outside the viewer's org comes back null; keep the views that did load
        data = partialGraphqlData(error, "listComposedForecasts", "fetchComposedForecasts");
      }
      return data.listComposedForecasts.items.filter(Boolean);
    } catch (error) {
      console.error("Error fetching composed forecasts:", error);
      return thunkAPI.rejectWithValue("Failed to fetch composed forecasts");
    }
  }
);

// Create a composed forecast with its data sources
export const createComposedForecast = createAsyncThunk(
  "composedForecasts/create",
  async (
    {
      forecast,
      dataSources,
    }: {
      forecast: Record<string, any>;
      dataSources: DataSourceFormState[];
    },
    thunkAPI
  ) => {
    try {
      // Create the forecast first
      const { data: forecastData } = await client.graphql({
        query: createComposedForecastCustom,
        variables: { input: forecast },
      });
      const createdForecast = forecastData.createComposedForecast;

      // Create all data sources in parallel. They take the saved forecast's owner and org (AppSync fills the owner
      // when the input left it out), so org members can read every source of a forecast they can read.
      const createdDataSources = await Promise.all(
        dataSources.map(async (ds) => {
          const input = buildDataSourceInput(
            ds,
            createdForecast.id,
            createdForecast.owner ?? forecast.owner,
            createdForecast.organizationId ?? forecast.organizationId ?? undefined
          );
          const { data: dsData } = await client.graphql({
            query: createComposedForecastDataSourceCustom,
            variables: { input },
          });
          return dsData.createComposedForecastDataSource;
        })
      );

      // Return the full forecast with data sources
      return {
        ...createdForecast,
        dataSources: { items: createdDataSources },
      };
    } catch (error) {
      console.error("Error creating composed forecast:", error);
      return thunkAPI.rejectWithValue("Failed to create composed forecast");
    }
  }
);

// Update a composed forecast
export const updateComposedForecast = createAsyncThunk(
  "composedForecasts/update",
  async ({ id, updates }: { id: string; updates: Record<string, any> }, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: updateComposedForecastCustom,
        variables: { input: { id, ...updates } },
      });
      return data.updateComposedForecast;
    } catch (error) {
      console.error("Error updating composed forecast:", error);
      return thunkAPI.rejectWithValue("Failed to update composed forecast");
    }
  }
);

// Delete a composed forecast and all its data sources
export const deleteComposedForecast = createAsyncThunk(
  "composedForecasts/delete",
  async ({ id, dataSources }: { id: string; dataSources: { id: string }[] }, thunkAPI) => {
    try {
      // Delete all child data sources in parallel. Use Promise.all so any
      // child failure aborts before the parent delete — otherwise we'd orphan
      // ComposedForecastDataSource rows with no reachable parent.
      await Promise.all(
        dataSources.map((ds) =>
          client.graphql({
            query: deleteComposedForecastDataSourceCustom,
            variables: { input: { id: ds.id } },
          })
        )
      );

      // Delete the forecast itself
      await client.graphql({
        query: deleteComposedForecastCustom,
        variables: { input: { id } },
      });

      return id;
    } catch (error) {
      console.error("Error deleting composed forecast:", error);
      return thunkAPI.rejectWithValue("Failed to delete composed forecast");
    }
  }
);

const graphqlErrorMessage = (error: any, fallback: string): string =>
  error?.errors?.[0]?.message || error?.message || fallback;

// Copy a composed forecast and its data sources (never its asset links). Sources are created one at a time so
// the modal can count them; any failure deletes whatever was created, so a half-built copy never stays behind.
export const copyComposedForecast = createAsyncThunk(
  "composedForecasts/copy",
  async (
    {
      original,
      name,
      ownership,
      onProgress,
    }: {
      original: ComposedForecast;
      name: string;
      ownership: ForecastOwnership;
      onProgress?: (progress: CopyProgress) => void;
    },
    thunkAPI
  ) => {
    const sources = original.dataSources?.items || [];
    const sourcesTotal = sources.length;
    let createdForecastId: string | null = null;
    const createdSources: any[] = [];
    let step: CopyProgress["phase"] = "view";

    try {
      onProgress?.({ phase: "view", sourcesDone: 0, sourcesTotal });
      const { data: forecastData } = await client.graphql({
        query: createComposedForecastCustom,
        variables: { input: buildCopyForecastInput(original, name, ownership) },
      });
      const createdForecast = forecastData.createComposedForecast;
      createdForecastId = createdForecast.id;

      // Sources take the saved copy's owner and org (AppSync stamps the owner when the input left it out)
      const owner = createdForecast.owner ?? ownership.owner;
      const organizationId = createdForecast.organizationId ?? ownership.organizationId;
      step = "sources";
      onProgress?.({ phase: "sources", sourcesDone: 0, sourcesTotal });
      for (const source of sources) {
        const { data: dsData } = await client.graphql({
          query: createComposedForecastDataSourceCustom,
          variables: { input: buildCopyDataSourceInput(source, createdForecast.id, owner, organizationId) },
        });
        // The mutation doesn't return the nested model output the cards read, so keep the original's
        createdSources.push({ ...source, ...dsData.createComposedForecastDataSource });
        onProgress?.({ phase: "sources", sourcesDone: createdSources.length, sourcesTotal });
      }

      onProgress?.({ phase: "done", sourcesDone: sourcesTotal, sourcesTotal });
      return {
        ...createdForecast,
        linkedAssets: { items: [] },
        dataSources: { items: createdSources },
      };
    } catch (error) {
      console.error("Error copying composed forecast:", error);
      const message = graphqlErrorMessage(error, "Failed to duplicate custom view");
      const base = { sourcesDone: createdSources.length, sourcesTotal, failedAt: step };
      let rolledBack: boolean | undefined;
      if (createdForecastId) {
        rolledBack = true;
        onProgress?.({ phase: "rollback", ...base });
        try {
          // Children first, so a failed parent delete never leaves sources with no reachable parent
          await Promise.all(
            createdSources.map((ds) =>
              client.graphql({ query: deleteComposedForecastDataSourceCustom, variables: { input: { id: ds.id } } })
            )
          );
          await client.graphql({ query: deleteComposedForecastCustom, variables: { input: { id: createdForecastId } } });
        } catch (rollbackError) {
          console.error("Error undoing partial composed forecast copy:", rollbackError);
          rolledBack = false;
        }
      }
      onProgress?.({ phase: "failed", ...base, error: message, rolledBack });
      return thunkAPI.rejectWithValue({ message, rolledBack: rolledBack ?? true, partialId: rolledBack === false ? createdForecastId : null });
    }
  }
);

// Add a data source to an existing forecast
export const addDataSource = createAsyncThunk(
  "composedForecasts/addDataSource",
  async (
    {
      composedForecastId,
      dataSource,
      owner,
      organizationId,
    }: {
      composedForecastId: string;
      dataSource: DataSourceFormState;
      owner?: string;
      organizationId?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const input = buildDataSourceInput(dataSource, composedForecastId, owner, organizationId);
      const result = await client.graphql({
        query: createComposedForecastDataSourceCustom,
        variables: { input },
      });
      if (result.errors?.length) {
        console.error("GraphQL errors creating data source:", result.errors);
        return thunkAPI.rejectWithValue(result.errors[0]?.message || "GraphQL error");
      }
      return {
        composedForecastId,
        dataSource: result.data.createComposedForecastDataSource,
      };
    } catch (error) {
      console.error("Error adding data source:", error);
      return thunkAPI.rejectWithValue(error?.errors?.[0]?.message || "Failed to add data source");
    }
  }
);

// Update an existing data source
export const updateDataSource = createAsyncThunk(
  "composedForecasts/updateDataSource",
  async (
    {
      composedForecastId,
      dataSource,
      owner,
      organizationId,
    }: {
      composedForecastId: string;
      dataSource: DataSourceFormState;
      owner?: string;
      organizationId?: string | null;
    },
    thunkAPI
  ) => {
    try {
      if (!dataSource.id) return thunkAPI.rejectWithValue("Cannot update data source without an ID");
      const input = buildDataSourceInput(dataSource, composedForecastId, owner, organizationId);
      input.id = dataSource.id;
      const { data } = await client.graphql({
        query: updateComposedForecastDataSourceCustom,
        variables: { input },
      });
      return {
        composedForecastId,
        dataSource: data.updateComposedForecastDataSource,
      };
    } catch (error) {
      console.error("Error updating data source:", error);
      return thunkAPI.rejectWithValue(error?.errors?.[0]?.message || "Failed to update data source");
    }
  }
);

// Point saved data sources at their forecast's owner and org without touching their configuration (an org change,
// or a source saved before the forecast had an org). The caller refetches the list afterwards.
export const restampDataSources = createAsyncThunk(
  "composedForecasts/restampDataSources",
  async (
    { ids, owner, organizationId }: { ids: string[]; owner?: string; organizationId: string | null },
    thunkAPI
  ) => {
    try {
      await Promise.all(
        ids.map((id) =>
          client.graphql({
            query: updateComposedForecastDataSourceCustom,
            variables: { input: { id, organizationId, ...(owner ? { owner } : {}) } },
          })
        )
      );
      return ids;
    } catch (error) {
      console.error("Error updating data source ownership:", error);
      return thunkAPI.rejectWithValue("Failed to update data source organization");
    }
  }
);

// Remove a data source
export const removeDataSource = createAsyncThunk(
  "composedForecasts/removeDataSource",
  async (
    { id, composedForecastId }: { id: string; composedForecastId: string },
    thunkAPI
  ) => {
    try {
      await client.graphql({
        query: deleteComposedForecastDataSourceCustom,
        variables: { input: { id } },
      });
      return { id, composedForecastId };
    } catch (error) {
      console.error("Error removing data source:", error);
      return thunkAPI.rejectWithValue("Failed to remove data source");
    }
  }
);

// Fetch existing ComposedForecastAssets join rows for a single asset.
// Returns the raw link rows (with nested composedForecast) so callers can
// both seed the picker checkbox state AND know which rows to delete on save.
export const fetchComposedForecastAssetLinks = createAsyncThunk(
  "composedForecasts/fetchAssetLinks",
  async (assetId: string, thunkAPI) => {
    try {
      const { data } = await client.graphql({
        query: composedForecastAssetsByAssetCustom,
        variables: { assetID: assetId },
      });
      return data.composedForecastAssetsByAssetIDAndComposedForecastID.items;
    } catch (error) {
      console.error("Error fetching composed forecast asset links:", error);
      return thunkAPI.rejectWithValue("Failed to fetch composed forecast asset links");
    }
  }
);

// Reconcile join-table rows for a single asset against a selection of
// composed forecasts (each with an optional per-link icon). Pure diff lives
// in composedForecastDashboardUtils; the thunk only runs the GraphQL.
// Mirrors deleteComposedForecast's Promise.all child-row pattern above.
//
// Per-link icon changes use updateComposedForecastAssetsCustom (not
// delete+recreate) to preserve the row id and createdAt.
export const syncComposedForecastAssetLinks = createAsyncThunk(
  "composedForecasts/syncAssetLinks",
  async (
    {
      assetId,
      selectedForecasts,
      existingLinks,
      owner,
      organizationId,
    }: {
      assetId: string;
      selectedForecasts: SelectedComposedForecast[];
      existingLinks: ComposedForecastAssetLinkRow[];
      owner?: string;
      organizationId?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const { toCreate, toDelete, toUpdate } = diffComposedForecastLinks(
        selectedForecasts,
        existingLinks
      );

      // allSettled: every change is attempted, and a refused one (link rows are owner-only to update) is reported
      // rather than hiding behind the first failure
      const settled = await Promise.allSettled([
        ...toCreate.map((sel) =>
          client.graphql({
            query: createComposedForecastAssetsCustom,
            variables: {
              input: {
                composedForecastID: sel.composedForecastID,
                assetID: assetId,
                ...(sel.icon ? { icon: sel.icon } : {}),
                ...(sel.isDefault ? { isDefault: true } : {}),
                ...(owner ? { owner } : {}),
                ...(organizationId ? { organizationId } : {}),
              },
            },
          })
        ),
        ...toUpdate.map((row) =>
          client.graphql({
            query: updateComposedForecastAssetsCustom,
            variables: {
              input: {
                id: row.id,
                icon: row.icon,
                isDefault: row.isDefault,
              },
            },
          })
        ),
        ...toDelete.map((row) =>
          client.graphql({
            query: deleteComposedForecastAssetsCustom,
            variables: { input: { id: row.id } },
          })
        ),
      ]);
      const failed = settled.filter((r) => r.status === "rejected").length;
      if (failed) {
        console.error("Some composed forecast link changes failed:", settled.filter((r) => r.status === "rejected"));
        return thunkAPI.rejectWithValue(
          `${failed} of ${settled.length} composed forecast change${settled.length === 1 ? "" : "s"} couldn't be saved. ` +
            "Only whoever attached a forecast to this asset (or an Admin) can change or remove it."
        );
      }

      return {
        assetId,
        created: toCreate,
        updated: toUpdate,
        deleted: toDelete.map((r) => r.id),
      };
    } catch (error) {
      console.error("Error syncing composed forecast asset links:", error);
      return thunkAPI.rejectWithValue("Failed to sync composed forecast asset links");
    }
  }
);

// Link one asset to a composed forecast from the forecast's side (the detail modal). The returned row carries the
// asset's id and name so the forecast's linkedAssets list updates without a refetch.
export const addComposedForecastAssetLink = createAsyncThunk(
  "composedForecasts/addAssetLink",
  async (
    {
      composedForecastId,
      asset,
      owner,
      organizationId,
    }: {
      composedForecastId: string;
      asset: { id: string; name: string; assetGroupId?: string };
      owner?: string;
      organizationId?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const { data } = await client.graphql({
        query: createComposedForecastAssetsCustom,
        variables: {
          input: {
            composedForecastID: composedForecastId,
            assetID: asset.id,
            ...(owner ? { owner } : {}),
            ...(organizationId ? { organizationId } : {}),
          },
        },
      });
      const row = data.createComposedForecastAssets;
      return { composedForecastId, link: { ...row, asset: { id: asset.id, name: asset.name, assetGroupId: asset.assetGroupId } } };
    } catch (error) {
      console.error("Error linking asset to composed forecast:", error);
      return thunkAPI.rejectWithValue("Couldn't assign the asset to this custom view");
    }
  }
);

// Remove one asset link by its join-row id. Owner auth: only the link's creator (or an Admin) can delete it.
export const removeComposedForecastAssetLink = createAsyncThunk(
  "composedForecasts/removeAssetLink",
  async ({ composedForecastId, linkId }: { composedForecastId: string; linkId: string }, thunkAPI) => {
    try {
      await client.graphql({
        query: deleteComposedForecastAssetsCustom,
        variables: { input: { id: linkId } },
      });
      return { composedForecastId, linkId };
    } catch (error) {
      console.error("Error unlinking asset from composed forecast:", error);
      return thunkAPI.rejectWithValue("Couldn't remove the asset (only whoever assigned it, or an Admin, can)");
    }
  }
);

/** Whether a source's display settings change anything (an untouched panel saves nothing). */
const hasDisplaySettings = (display: SourceDisplaySettings): boolean =>
  !!display.color ||
  !!display.errorColor ||
  Object.values(display.channels || {}).some((c) => c && (c.visible === false || !!c.color));

// Build GraphQL input from form state
function buildDataSourceInput(
  formState: DataSourceFormState,
  composedForecastId: string,
  owner?: string,
  organizationId?: string | null
): Record<string, any> {
  // DATASET is not yet in the GraphQL enum — persist as S3_DATA until backend migration adds it
  const persistedSourceType = formState.sourceType === "DATASET" ? "S3_DATA" : formState.sourceType;

  const input: Record<string, any> = {
    composedForecastId,
    sourceType: persistedSourceType,
    sourceName: formState.sourceName,
    sourceDescription: formState.sourceDescription || "",
    weight: normalizeWeight(formState.weight),
    priority: formState.priority ?? 0,
    isActive: true,
    owner,
    organizationId,
  };

  if (formState.fallbackStrategy) input.fallbackStrategy = formState.fallbackStrategy;
  if (formState.timeAlignment) input.timeAlignment = formState.timeAlignment;
  if (formState.temporalResolution) input.temporalResolution = formState.temporalResolution;

  switch (formState.sourceType) {
    case "POSTGRES_HISTORICAL":
      if (formState.parameterId) {
        input.postgresTable = formState.datasourceName || "";
        input.postgresColumns = [String(formState.parameterId)];
        input.metadata = JSON.stringify({
          parameterId: formState.parameterId,
          parameterName: formState.parameterName,
          datasourceName: formState.datasourceName,
          lookbackDays: formState.lookbackDays,
        });
      }
      break;

    case "PROCESSED_MODEL_OUTPUT":
      if (formState.processedModelOutputId) {
        input.processedModelOutputId = formState.processedModelOutputId;
      }
      break;

    case "DATASET":
      if (formState.datasetId) {
        // A curated dataset is read by column over a range (stage 5, decision 4); others keep the pinned file
        input.metadata = JSON.stringify(
          formState.curated
            ? {
                datasetId: formState.datasetId,
                datasetName: formState.datasetName,
                curated: true,
                column: formState.column,
                lookbackDays: formState.lookbackDays,
              }
            : {
                datasetId: formState.datasetId,
                datasetName: formState.datasetName,
                datasetRecordId: formState.datasetRecordId,
                datasetRecordS3Path: formState.datasetRecordS3Path,
                datasetRecordFileName: formState.datasetRecordFileName,
              }
        );
      }
      if (!formState.curated && formState.operation && formState.operation.id) {
        input.dataOperations = JSON.stringify({ pipeline: [formState.operation] });
      } else {
        input.dataOperations = null;
      }
      break;
  }

  // The observation flag, the display settings and a model output's chosen variable ride in metadata; turning them off
  // rewrites the metadata without them (a processed-model source carries no other metadata).
  const extras: Record<string, unknown> = {};
  if (formState.accuracy?.enabled) extras.accuracy = formState.accuracy;
  if (formState.sourceType === "PROCESSED_MODEL_OUTPUT" && formState.variation) extras.variation = formState.variation;
  // The error line colour only means something on the observation — dropped once the source stops being one
  const display = formState.display && !formState.accuracy?.enabled
    ? { ...formState.display, errorColor: undefined }
    : formState.display;
  if (display && hasDisplaySettings(display)) extras.display = display;
  if (typeof formState.drawOrder === "number") extras.drawOrder = formState.drawOrder;
  if (formState.historyDays && formState.historyDays > 0) extras.historyDays = formState.historyDays;
  if (formState.historyAnchor === "today") extras.historyAnchor = "today";
  if (Object.keys(extras).length) {
    const meta = input.metadata ? JSON.parse(input.metadata) : {};
    input.metadata = JSON.stringify({ ...meta, ...extras });
  } else if (formState.sourceType === "PROCESSED_MODEL_OUTPUT") {
    input.metadata = null;
  }

  return input;
}

const composedForecastSlice = createSlice({
  name: "composedForecasts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchComposedForecasts
      .addCase(fetchComposedForecasts.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(fetchComposedForecasts.fulfilled, (state, action) => {
        state.loading = "success";
        state.forecasts = {};
        for (const forecast of action.payload) {
          state.forecasts[forecast.id] = forecast;
        }
      })
      .addCase(fetchComposedForecasts.rejected, (state, action) => {
        state.loading = "error";
        state.error = action.payload as string;
      })

      // createComposedForecast
      .addCase(createComposedForecast.fulfilled, (state, action) => {
        state.forecasts[action.payload.id] = action.payload;
      })
      .addCase(copyComposedForecast.fulfilled, (state, action) => {
        state.forecasts[action.payload.id] = action.payload;
      })

      // updateComposedForecast
      .addCase(updateComposedForecast.fulfilled, (state, action) => {
        const existing = state.forecasts[action.payload.id];
        if (existing) {
          state.forecasts[action.payload.id] = {
            ...existing,
            ...action.payload,
            dataSources: existing.dataSources, // preserve nested data sources
          };
        }
      })

      // deleteComposedForecast
      .addCase(deleteComposedForecast.fulfilled, (state, action) => {
        delete state.forecasts[action.payload];
      })

      // addDataSource
      .addCase(addDataSource.fulfilled, (state, action) => {
        const forecast = state.forecasts[action.payload.composedForecastId];
        if (forecast) {
          forecast.dataSources.items.push(action.payload.dataSource);
        }
      })

      // updateDataSource
      .addCase(updateDataSource.fulfilled, (state, action) => {
        const forecast = state.forecasts[action.payload.composedForecastId];
        if (forecast) {
          const updated = action.payload.dataSource;
          // The update mutation omits the nested processedModelOutput, so keep the loaded one while the output id is unchanged
          forecast.dataSources.items = forecast.dataSources.items.map((ds) =>
            ds.id !== updated.id
              ? ds
              : {
                  ...updated,
                  processedModelOutput:
                    updated.processedModelOutput ??
                    (updated.processedModelOutputId === ds.processedModelOutputId ? ds.processedModelOutput : undefined),
                }
          );
        }
      })

      // removeDataSource
      .addCase(removeDataSource.fulfilled, (state, action) => {
        const forecast = state.forecasts[action.payload.composedForecastId];
        if (forecast) {
          forecast.dataSources.items = forecast.dataSources.items.filter(
            (ds) => ds.id !== action.payload.id
          );
        }
      })

      // Asset links made or removed from the forecast's side
      .addCase(addComposedForecastAssetLink.fulfilled, (state, action) => {
        const forecast: any = state.forecasts[action.payload.composedForecastId];
        if (forecast) {
          const items = forecast.linkedAssets?.items ?? [];
          forecast.linkedAssets = { ...(forecast.linkedAssets || {}), items: [...items, action.payload.link] };
        }
      })
      .addCase(removeComposedForecastAssetLink.fulfilled, (state, action) => {
        const forecast: any = state.forecasts[action.payload.composedForecastId];
        if (forecast?.linkedAssets?.items) {
          forecast.linkedAssets.items = forecast.linkedAssets.items.filter((link: any) => link?.id !== action.payload.linkId);
        }
      });
  },
});

export default composedForecastSlice.reducer;
